import React, { useEffect, useRef, useState } from "react";
import { Copy, ClipboardPaste, CopyPlus, Trash2, Plus, ChevronRight, Check, SquareStack, PanelRight } from "lucide-react";

interface SubMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  /** 'card' renders as a visual card toggle (icon + label) */
  variant?: 'card';
  children?: SubMenuItem[];
}

type MenuEntry =
  | { type: 'header'; label: string }
  | { type: 'separator' }
  | {
      type?: 'item';
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
      disabled?: boolean;
      danger?: boolean;
      shortcut?: string;
      children?: SubMenuItem[];
    };

interface ContextMenuProps {
  x: number;
  y: number;
  items: MenuEntry[];
  onClose: () => void;
}

/** Card-style option (like the Modal/Sidebar toggle) */
const CardOption: React.FC<{ item: SubMenuItem; onClose: () => void }> = ({ item, onClose }) => (
  <button
    onClick={() => { item.onClick(); onClose(); }}
    className={`flex flex-col items-center gap-1.5 px-4 py-3 rounded-lg border-2 transition-all min-w-[80px]
      ${item.active
        ? 'border-blue-500 bg-blue-50'
        : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
      }`}
  >
    <span className={`w-6 h-6 ${item.active ? 'text-blue-600' : 'text-gray-500'}`}>
      {item.icon || (item.label === 'Modal' ? <SquareStack className="w-6 h-6" /> : <PanelRight className="w-6 h-6" />)}
    </span>
    <span className={`text-xs font-medium ${item.active ? 'text-blue-700' : 'text-gray-600'}`}>
      {item.label}
    </span>
  </button>
);

/** Hook for delayed submenu close — gives user time to move mouse diagonally */
function useSubmenuHover() {
  const [open, setOpen] = useState<number | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = (index: number) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(index);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(null), 180);
  };
  // Keep submenu open while hovering the submenu panel itself
  const handleSubmenuEnter = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  return { open, handleEnter, handleLeave, handleSubmenuEnter };
}

/** Rich submenu panel — supports card variants and nested submenus */
const RichSubMenuPanel: React.FC<{ items: SubMenuItem[]; sectionHeader?: string; onClose: () => void }> = ({ items, sectionHeader, onClose }) => {
  const { open: openChild, handleEnter, handleLeave, handleSubmenuEnter } = useSubmenuHover();
  const cardItems = items.filter(i => i.variant === 'card');
  const listItems = items.filter(i => i.variant !== 'card');

  return (
    <div className="absolute left-full top-0 bg-white rounded-xl shadow-xl border border-gray-200 py-2 ml-0 whitespace-nowrap min-w-[160px]">
      {/* Invisible hover bridge — fills gap between parent and this panel */}
      <div className="absolute right-full top-0 w-3 h-full" />
      {sectionHeader && (
        <div className="px-3 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          {sectionHeader}
        </div>
      )}

      {/* Card-style options */}
      {cardItems.length > 0 && (
        <div className="flex gap-2 px-3 py-1.5">
          {cardItems.map((item, i) => (
            <CardOption key={i} item={item} onClose={onClose} />
          ))}
        </div>
      )}

      {/* Separator between cards and list items */}
      {cardItems.length > 0 && listItems.length > 0 && (
        <div className="my-1.5 border-t border-gray-100 mx-2" />
      )}

      {/* Regular list items */}
      {listItems.map((sub, j) => {
        const hasChildren = sub.children && sub.children.length > 0;
        return (
          <div
            key={j}
            className="relative"
            onMouseEnter={() => hasChildren && handleEnter(j)}
            onMouseLeave={() => hasChildren && handleLeave()}
          >
            <button
              onClick={() => {
                if (hasChildren) return;
                sub.onClick();
                onClose();
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
            >
              {sub.icon && <span className="w-4 h-4 flex-shrink-0 text-gray-500">{sub.icon}</span>}
              <span className="flex-1">{sub.label}</span>
              {hasChildren && <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
            </button>
            {hasChildren && openChild === j && (
              <div onMouseEnter={handleSubmenuEnter} onMouseLeave={handleLeave}>
                <RichSubMenuPanel items={sub.children!} onClose={onClose} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { open: openSubmenu, handleEnter: handleSubmenuOpen, handleLeave: handleSubmenuClose, handleSubmenuEnter: handleSubmenuPanelEnter } = useSubmenuHover();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as HTMLElement)) {
        onClose();
      }
    };
    const handleScroll = () => onClose();

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [onClose]);

  // Adjust position so menu doesn't overflow viewport
  useEffect(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      menuRef.current.style.left = `${x - rect.width}px`;
    }
    if (rect.bottom > window.innerHeight) {
      menuRef.current.style.top = `${y - rect.height}px`;
    }
  }, [x, y]);

  return (
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[180px] bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 animate-in fade-in zoom-in-95 duration-100"
      style={{ left: x, top: y }}
    >
      {items.map((entry, i) => {
        // Section header
        if ('type' in entry && entry.type === 'header') {
          return (
            <div key={i} className="px-3 pt-1 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              {entry.label}
            </div>
          );
        }

        // Separator
        if ('type' in entry && entry.type === 'separator') {
          return <div key={i} className="my-1 border-t border-gray-100 mx-2" />;
        }

        // Regular menu item
        const item = entry as Extract<MenuEntry, { label: string; icon: React.ReactNode }>;
        const hasChildren = item.children && item.children.length > 0;
        const isOpen = openSubmenu === i;

        return (
          <div
            key={i}
            className="relative"
            onMouseEnter={() => hasChildren && handleSubmenuOpen(i)}
            onMouseLeave={() => hasChildren && handleSubmenuClose()}
          >
            <button
              disabled={item.disabled}
              onClick={() => {
                if (hasChildren) return;
                item.onClick();
                onClose();
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left rounded-md mx-0
                ${item.disabled
                  ? "text-gray-300 cursor-not-allowed"
                  : item.danger
                    ? "text-red-500 hover:bg-red-50"
                    : isOpen
                      ? "bg-blue-500 text-white"
                      : "text-gray-700 hover:bg-gray-50"
                }`}
            >
              <span className={`w-4 h-4 flex-shrink-0 ${isOpen ? 'text-white' : ''}`}>{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.shortcut && (
                <span className={`ml-4 text-xs ${isOpen ? 'text-blue-200' : 'text-gray-400'}`}>{item.shortcut}</span>
              )}
              {hasChildren && (
                <ChevronRight className={`w-3.5 h-3.5 ml-auto ${isOpen ? 'text-white' : 'text-gray-400'}`} />
              )}
            </button>

            {/* Rich Submenu */}
            {hasChildren && isOpen && (
              <div onMouseEnter={handleSubmenuPanelEnter} onMouseLeave={handleSubmenuClose}>
                <RichSubMenuPanel items={item.children!} onClose={onClose} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export { Copy, ClipboardPaste, CopyPlus, Trash2, Plus };
